import {api} from "@/shared/api";

import {WorkingTimeRequestRevisionsTable} from "@/features/requests/workingTimeRequestRevisionsTable";
import {SubstituteValidation} from "@/shared/workingtime/substituteValidation";
import {WorkingTimeRequestUtil} from "@/shared/workingtime/workingTimeRequestUtil";
import {WorkingTimeRequestStepsComponent} from "./workingTimeRequestStepsComponent";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {
	CustomFieldsForRecordType,
	ProofReviewAction,
	ProofStatus,
	VacationUnit,
	WorkingTimeRequestEvent,
	WorkingTimeRequestStatus,
	WorkingTimeRequestStepConfigurationType,
	WorkingTimeRequestStepType,
	WorkingTimeTypeCategory
} from "@/shared/timrTypes";
import {
	AbstractWorkingTimeRequestResponseModel,
	CustomFieldDefinition,
	WorkingTimeRequestCalculationResponse,
	WorkingTimeRequestCalculationTimeAccountData
} from "@/shared/timr";
import {
	calculateDurationBetween,
	formatDate,
	formatDuration,
	formatVacation,
	getCurrentISOOffset,
	getISOZonedDateTime,
	parseISODate,
	parseISOZonedDateTime
} from "@/shared/utils/datefunctions";
import {ProofAttachmentGallery} from "@/shared/workingtime/proofAttachmentGallery";
import {ButtonRadioGroup, TypedButtonDefinition} from "@/shared/ui/buttonRadioGroup";
import {SecurityStatus} from "@/shared/securityStatus";


enum WorkingTimeRequestApprovalDialogMode { Edit = "Edit", Show = "Show" }

export interface WorkingTimeRequestApprovalDialogConfig {
	dialogId: string;
	isGroupLeaderForThemself: boolean;
	customFieldDefinitions: CustomFieldDefinition[];
}

export class WorkingTimeRequestApprovalDialog {

	public static EVENT_REQUEST_APPROVED = "approved";
	public static EVENT_REQUEST_REJECTED = "rejected";

	private readonly dom: JQuery;
	private dialog: BootstrapModal.Modal;
	protected dialogMode: WorkingTimeRequestApprovalDialogMode;
	private workingTimeRequestRevisionsTable: WorkingTimeRequestRevisionsTable;
	private workingTimeRequest: AbstractWorkingTimeRequestResponseModel;
	private substituteValidation: SubstituteValidation;
	private approvalWorkingTimeRequestStepsComponent: WorkingTimeRequestStepsComponent;
	private cancellationWorkingTimeRequestStepsComponent: WorkingTimeRequestStepsComponent;

	private proofAttachmentGallery: ProofAttachmentGallery;
	private proofReviewActionButtonGroup: ButtonRadioGroup<ProofReviewAction>;

	private isAllowedToViewFullWorkingTimeRequestRequest: JQuery.jqXHR;

	constructor(public config: WorkingTimeRequestApprovalDialogConfig) {
		this.dom = $(this.config.dialogId);
		this.init();
	}

	public show(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		this.dialogMode = WorkingTimeRequestApprovalDialogMode.Show;
		this.showRecording(workingTimeRequest);
	}

	public edit(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		this.dialogMode = WorkingTimeRequestApprovalDialogMode.Edit;
		this.showRecording(workingTimeRequest);
	}

	private init() {
		this.dialog = new BootstrapModal.Modal({
			title: messages["requests.workingTimeRequestApproval.title"],
			message: this.dom,
			size: "modal-xl",
			css: {
				content: "border-NOT_INITIALIZED"
			},
			buttons: [{
				text: "confirmation.approve",
				action: (modal) => {
					this.checkForProofReviewAction("approve")
						.then(() => {
							this.closeDialog();
							this.approve();
						})
						.catch(() => modal.resetLoadingButtons());
				},
				css: "approveButton btn-success",
			}, {
				text: "confirmation.reject",
				action: (modal) => {
					this.checkForProofReviewAction("reject")
						.then(() => {
							this.closeDialog();
							this.reject();
						})
						.catch(() => modal.resetLoadingButtons());
				},
				css: "rejectButton btn-danger"
			}, {
				text: "confirmation.approveCancellation",
				action: () => {
					this.closeDialog();
					this.approveCancellation();
				},
				css: "approveCancellationButton btn-success",
			}, {
				text: "confirmation.rejectCancellation",
				action: () => {
					this.closeDialog();
					this.rejectCancellation();
				},
				css: "rejectCancellationButton btn-danger"
			}, {
				text: "dialog.cancel",
				action: () => {
					this.closeDialog();
				},
				css: "cancelButton btn-default"
			}, {
				text: "requests.workingTimeRequestsUser.context.approveImmediately",
				action: () => WorkingTimeRequestUtil.approveWorkingTimeRequestImmediately(this.workingTimeRequest, () => {
					this.closeDialog();
					this.onWorkingTimeRequestApproved();
				}).onClose(() => this.dialog.resetLoadingButtons()),
				left: true,
				css: "approveImmediatelyButton btn-success"
			}, {
				text: "requests.workingTimeRequestsUser.context.rejectImmediately",
				action: () => WorkingTimeRequestUtil.rejectWorkingTimeRequestImmediately(this.workingTimeRequest, () => {
					this.closeDialog();
					this.onWorkingTimeRequestRejected();
				}).onClose(() => this.dialog.resetLoadingButtons()),
				left: true,
				css: "rejectImmediatelyButton btn-danger"
			}, {
				text: "requests.workingTimeRequestsUser.context.approveCancellationImmediately",
				action: () => WorkingTimeRequestUtil.approveCancellationWorkingTimeRequestImmediately(this.workingTimeRequest, () => {
					this.closeDialog();
					this.onWorkingTimeRequestApproved();
				}).onClose(() => this.dialog.resetLoadingButtons()),
				left: true,
				css: "approveCancellationImmediatelyButton btn-success"
			}, {
				text: "requests.workingTimeRequestsUser.context.rejectCancellationImmediately",
				action: () => WorkingTimeRequestUtil.rejectCancellationWorkingTimeRequestImmediately(this.workingTimeRequest, () => {
					this.closeDialog();
					this.onWorkingTimeRequestRejected();
				}).onClose(() => this.dialog.resetLoadingButtons()),
				left: true,
				css: "rejectCancellationImmediatelyButton btn-danger"
			}, {
				text: "requests.workingTimeRequestsUser.context.cancelImmediately",
				action: () => WorkingTimeRequestUtil.cancelImmediatelyWorkingTimeRequest(this.workingTimeRequest, () => {
					this.closeDialog();
					this.onWorkingTimeRequestRejected();
				}).onClose(() => this.dialog.resetLoadingButtons()),
				left: true,
				css: "cancelImmediatelyButton btn-danger"
			}],
			onOpen: () => {
				this.updateBorder();
			},
			onClose: () => {
				this.closeDialog();
			}
		});
		this.initWorkingTimeRequestRevisionsTable();
		this.initSubstituteValidation();
		this.initProofs();
		this.approvalWorkingTimeRequestStepsComponent = document.getElementById("workingTimeRequestStepsApproval") as WorkingTimeRequestStepsComponent;
		this.cancellationWorkingTimeRequestStepsComponent = document.getElementById("workingTimeRequestStepsCancellation") as WorkingTimeRequestStepsComponent;
	}

	private initWorkingTimeRequestRevisionsTable() {
		this.workingTimeRequestRevisionsTable = new WorkingTimeRequestRevisionsTable({
			element: this.dom.find("#workingTimeRequestRevisionsTable"),
			orderable: false,
			clampNote: true,
			allowExpand: true
		});
		this.dom.find("#workingTimeRequestRevisionTableCollapseToggle").click(() => {
			this.dom.find("#workingTimeRequestRevisionTableCollapse").collapse("toggle");
		});
		this.dom.find("#workingTimeRequestRevisionTableCollapse")
			.on("show.bs.collapse", () => {
				this.dom.find("#workingTimeRequestRevisionTableCollapseTextShow").hide();
				this.dom.find("#workingTimeRequestRevisionTableCollapseTextHide").show();
			})
			.on("hide.bs.collapse", () => {
				this.dom.find("#workingTimeRequestRevisionTableCollapseTextShow").show();
				this.dom.find("#workingTimeRequestRevisionTableCollapseTextHide").hide();
			});
	}

	private updateTitle() {
		const titleMessage = WorkingTimeRequestUtil.isWorkingTimeNoticeRequest(this.workingTimeRequest)
			? messages[this.dialogMode === WorkingTimeRequestApprovalDialogMode.Show
				? "workingTimeNotice.dialog.title.showRequest"
				: "workingTimeNotice.dialog.title.editRequest"]
			: messages.withArguments("requests.workingTimeRequestApproval.title." + this.dialogMode.toLowerCase());
		this.dialog.setTitle(titleMessage);
	}

	private updateBorder() {
		const modalContent = this.dom.parents("div.modal-content");

		modalContent.removeClass("border-NOT_INITIALIZED");

		Object.keys(WorkingTimeTypeCategory)
			.map(k => WorkingTimeTypeCategory[k as keyof typeof WorkingTimeTypeCategory])
			.forEach(c => {
				modalContent.removeClass("border-" + c);

			});

		if (this.workingTimeRequest && this.workingTimeRequest.workingTimeType && this.workingTimeRequest.workingTimeType.category) {
			modalContent.addClass("border-" + this.workingTimeRequest.workingTimeType.category);
		}
	}

	private closeDialog() {
		this.dialog.close();
	}

	private showRecording(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		this.workingTimeRequest = workingTimeRequest;
		this.updateTitle();
		this.updateDialogButtons(workingTimeRequest);
		this.dom.find("#workingTimeRequestApprovalDialogStatus").removeClass().addClass(`label request-status-${workingTimeRequest.status} request-category-${workingTimeRequest.workingTimeType.category}`)
			.children("span").text(messages["workingTimeRequestStatus." + workingTimeRequest.status]);
		this.dom.find("#workingTimeRequestApprovalDialogProofReviewActionContainer")
			// Proof can only be reviewed in approval
			.toggle(this.dialogMode === WorkingTimeRequestApprovalDialogMode.Edit && WorkingTimeRequestUtil.isInApprovalProcess(workingTimeRequest));
		this.dom.find("#workingTimeRequestApprovalDialogStatusComment").text(workingTimeRequest.statusComment || "");
		this.dom.find(".status-comment").toggle(!!workingTimeRequest.statusComment);
		this.dom.find(".request-approval").toggle(!!workingTimeRequest.approvalSteps);
		this.dom.find(".request-cancellation").toggle(!!workingTimeRequest.cancellationSteps);
		this.dom.find("#workingTimeRequestApprovalDialogUser").text(workingTimeRequest.user.fullname);
		const noticeLabel = `<span class="badge badge-notice ml-10">${messages["requests.workingTimeRequestsUser.notice"]}</span>`;
		this.dom.find("#workingTimeRequestApprovalDialogWorkingTimeType")
			.text(workingTimeRequest.workingTimeType.name)
			.append(WorkingTimeRequestUtil.isWorkingTimeNoticeRequest(workingTimeRequest) ? noticeLabel : "");
		this.dom.find("#workingTimeRequestApprovalDialogRange").html(WorkingTimeRequestUtil.renderDateRange(workingTimeRequest));
		this.dom.find("#workingTimeRequestApprovalDialogDuration").html(WorkingTimeRequestUtil.isWorkingTimeRequest(workingTimeRequest)
			? formatDuration(calculateDurationBetween(
				getISOZonedDateTime(parseISOZonedDateTime(workingTimeRequest.start)),
				getISOZonedDateTime(parseISOZonedDateTime(workingTimeRequest.end)),
				workingTimeRequest.breakTimeManual))
			: null);
		this.dom.find("[data-substitute-row]").toggle(WorkingTimeRequestUtil.isWorkingTimeDateSpanRequest(workingTimeRequest) ? !!workingTimeRequest.substitute : false);
		this.dom.find("#workingTimeRequestApprovalDialogSubstitute").text((WorkingTimeRequestUtil.isWorkingTimeDateSpanRequest(workingTimeRequest) && workingTimeRequest.substitute?.fullname) || "");
		this.dom.find("#workingTimeRequestApprovalDialogDescription").text(workingTimeRequest.description || "");
		this.dom.find("#workingTimeRequestApprovalDialogBreakTimeManual").text(WorkingTimeRequestUtil.isWorkingTimeRequest(workingTimeRequest) ? workingTimeRequest.breakTimeManual ?? 0 : null);
		if (WorkingTimeRequestUtil.isWorkingTimeRequest(workingTimeRequest)) {
			const customFieldsContainer = this.dom.find("[data-custom-fields-container]").empty();
			for (const customFieldDefinition of this.config.customFieldDefinitions) {
				const value = workingTimeRequest[`customField${customFieldDefinition.fieldNumber}`];
				// Definitions without a name are disabled
				if (customFieldDefinition.fieldName?.trim() && value) {
					customFieldsContainer.append($("<dt>").append($("<span>").text(customFieldDefinition.fieldName)));
					if (customFieldDefinition.fieldType !== CustomFieldsForRecordType.Checkbox) {
						customFieldsContainer.append($("<dd>").append($("<span>").text(value)));
					} else {
						customFieldsContainer.append($("<dd>").append($("<input type=\"checkbox\" disabled>").prop("checked", value === "true")));
					}
				}
			}
		}

		this.dom.find("[data-show-for-minute-based]").toggle(WorkingTimeRequestUtil.isWorkingTimeRequest(workingTimeRequest));

		// Substitutes exist only on date span requests, and substituteValidation skips minute-based types anyway.
		const dateSpanRequest = WorkingTimeRequestUtil.isWorkingTimeDateSpanRequest(workingTimeRequest) ? workingTimeRequest : null;
		this.substituteValidation?.setWorkingTimeType(workingTimeRequest.workingTimeType);
		this.substituteValidation?.validateSubstitute(dateSpanRequest?.substitute ?? null);
		this.substituteValidation?.setStartEndDate(parseISODate(dateSpanRequest?.start), parseISODate(dateSpanRequest?.end));

		this.approvalWorkingTimeRequestStepsComponent.steps = workingTimeRequest.approvalSteps ?? [];
		this.cancellationWorkingTimeRequestStepsComponent.steps = workingTimeRequest.cancellationSteps ?? [];

		this.updateIfAllowedToViewFullRequest(workingTimeRequest);

		this.dialog.show();
	}

	private updateDialogButtons(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		const isEditMode = this.dialogMode === WorkingTimeRequestApprovalDialogMode.Edit;
		const immediateStatusChangeAllowed = WorkingTimeRequestUtil.isImmediateStatusChangeAllowed(workingTimeRequest, this.config.isGroupLeaderForThemself);
		const isCurrentApprovableStepApproval = workingTimeRequest.currentApprovableStep?.type === WorkingTimeRequestStepType.APPROVAL;
		const isCurrentApprovableStepCancellation = workingTimeRequest.currentApprovableStep?.type === WorkingTimeRequestStepType.CANCELLATION;
		this.dialog.toggleButton("approveButton", isEditMode && isCurrentApprovableStepApproval);
		this.dialog.toggleButton("rejectButton", isEditMode && isCurrentApprovableStepApproval);
		this.dialog.toggleButton("approveCancellationButton", isEditMode && isCurrentApprovableStepCancellation);
		this.dialog.toggleButton("rejectCancellationButton", isEditMode && isCurrentApprovableStepCancellation);
		this.dialog.toggleButton("approveImmediatelyButton", isEditMode && WorkingTimeRequestUtil.isInApprovalProcess(this.workingTimeRequest) && immediateStatusChangeAllowed);
		this.dialog.toggleButton("rejectImmediatelyButton", isEditMode && WorkingTimeRequestUtil.isInApprovalProcess(this.workingTimeRequest) && immediateStatusChangeAllowed);
		this.dialog.toggleButton("approveCancellationImmediatelyButton", isEditMode && WorkingTimeRequestUtil.isInCancellationProcess(this.workingTimeRequest) && immediateStatusChangeAllowed);
		this.dialog.toggleButton("rejectCancellationImmediatelyButton", isEditMode && WorkingTimeRequestUtil.isInCancellationProcess(this.workingTimeRequest) && immediateStatusChangeAllowed);
		this.dialog.toggleButton("cancelImmediatelyButton", workingTimeRequest.status === WorkingTimeRequestStatus.Approved && immediateStatusChangeAllowed);
	}

	private updateIfAllowedToViewFullRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		// Hide elements that are only visible when allowed to view full request
		this.showTimeAccountData(null);
		this.updateProofsView(workingTimeRequest, false);
		this.dom.find("[data-admin-or-leader-required]").hide();
		this.isAllowedToViewFullWorkingTimeRequestRequest?.abort();
		this.isAllowedToViewFullWorkingTimeRequestRequest = api("requests/getWorkingTimeRequestCalculationData.json")
			.get({
				userId: workingTimeRequest.user.id,
				workingTimeRequestId: workingTimeRequest.id,
				timeZone: getCurrentISOOffset()
			})
			.done((data: WorkingTimeRequestCalculationResponse) => {
				// Per definition in the refinement, substitues should never see any proofs
				// even if, for example, the same teamleader sees them a step later.
				const isSubstituteStep = workingTimeRequest.currentApprovableStep?.requiredApproverType === WorkingTimeRequestStepConfigurationType.SUBSTITUTE;
				this.updateProofsView(workingTimeRequest, !!data && !isSubstituteStep);
				if (data) {
					this.workingTimeRequestRevisionsTable.filterByWorkingTimeRequestId(workingTimeRequest.id);
					this.showTimeAccountData(data.timeAccountData);
					this.updateVacationAndWorkingDaysFields(data);
					this.dom.find("#workingTimeRequestHistoryRow").show();
					this.dom.find(".workingTimeTypeCategory-vacation").toggle(!!data.vacationData);
					this.dom.find(".workingTimeTypeCategory-non-vacation").toggle(!!data.workingDaysRequired);
					this.dom.find(".workingTimeTypeCategory-workDuration").toggle(workingTimeRequest.workingTimeType.category === WorkingTimeTypeCategory.UnpaidTimeOff);
				}
			});
	}

	private updateProofsView(workingTimeRequest: AbstractWorkingTimeRequestResponseModel, showProofs: boolean) {
		if (showProofs) {
			// If proof status is optional default to not reviewed as nothing can be reviewed
			this.proofReviewActionButtonGroup?.setValue(workingTimeRequest.proofStatus === ProofStatus.OPTIONAL ? ProofReviewAction.NOT_REVIEWED : null);
			this.proofAttachmentGallery?.loadProofAttachments({workingTimeRequestId: workingTimeRequest.id})
			this.proofAttachmentGallery?.update(workingTimeRequest.proofRequirement);
			if (workingTimeRequest.proofStatus) {
				this.dom.find("#workingTimeRequestApprovalDialogProofStatus").removeClass()
					.addClass(`label proof-status status-${workingTimeRequest.proofStatus}`)
					.children("span").text(messages["proofStatus." + workingTimeRequest.proofStatus]);
			}
			this.dom.find("[data-show-if-proof-available]").toggle(!!workingTimeRequest.proofId);
			// Hide proof review action if proof is optional as there is nothing to review
			// and also if not in approval process as there is no need to review if not in approval process even if a proof is available
			this.dom.find("#workingTimeRequestApprovalDialogProofReviewActionContainer")
				.toggle(workingTimeRequest.proofStatus !== ProofStatus.OPTIONAL &&
					this.dialogMode === WorkingTimeRequestApprovalDialogMode.Edit &&
					workingTimeRequest.currentApprovableStep?.type === WorkingTimeRequestStepType.APPROVAL
				);
		} else {
			this.dom.find("[data-show-if-proof-available]").toggle(false);
			this.proofAttachmentGallery?.reset();
			this.proofReviewActionButtonGroup?.setValue(workingTimeRequest.proofId ? ProofReviewAction.NOT_REVIEWED : null);
		}
	}

	private updateVacationAndWorkingDaysFields(viewData: WorkingTimeRequestCalculationResponse) {
		const durationDaysField = viewData.vacationData ?
			this.dom.find("#workingTimeRequestApprovalDialogRequiredVacation") :
			this.dom.find("#workingTimeRequestApprovalDialogRequiredWorkingDays");
		const vacationWarningField = this.dom.find("#workingTimeRequestApprovalDialogVacationWarning");
		durationDaysField.show();
		durationDaysField.text("-").next(".vacationUnitLabel").hide();
		if (viewData.vacationData) {
			durationDaysField.show();
			durationDaysField.text(formatVacation(viewData.vacationData.vacationRequired, viewData.vacationData.vacationUnit));
			durationDaysField.next(".vacationUnitLabel").text(viewData.vacationData.vacationUnit ? messages["vacationUnit." + viewData.vacationData.vacationUnit] : "").show();
			vacationWarningField.text(viewData.vacationData.warningText).toggle(!!viewData.vacationData.warningText);
		} else if (viewData.workingDaysRequired) {
			durationDaysField.show();
			durationDaysField.text(formatVacation(viewData.workingDaysRequired, VacationUnit.Days) + " " + messages["vacationUnit.DAYS"]);
			vacationWarningField.text(null).hide();
		} else {
			durationDaysField.text("?");
			vacationWarningField.text(null).hide();
		}
	}

	private showTimeAccountData(timeAccountData: WorkingTimeRequestCalculationTimeAccountData) {
		this.dom.find("#workingTimeRequestApprovalDialogWorkDurationLastPeriod")
			.text(timeAccountData && timeAccountData.lastWorkDurationTotalBalance != null ? formatDuration(timeAccountData.lastWorkDurationTotalBalance) : "-");
		this.dom.find("#workingTimeRequestApprovalDialogWorkDurationCurrentPeriod")
			.text(timeAccountData && timeAccountData.workDurationCurrentBalance != null ? formatDuration(timeAccountData.workDurationCurrentBalance) : "-");
		this.dom.find("#workingTimeRequestApprovalDialogWorkDurationTotal")
			.text(timeAccountData && timeAccountData.workDurationCurrentTotalBalance != null ? formatDuration(timeAccountData.workDurationCurrentTotalBalance) : "-");
		if (timeAccountData && timeAccountData.unpaidTimeOffDurationPlanned) {
			this.dom.find("#workingTimeRequestApprovalDialogUnpaidTimeOffDurationPlanned")
				.text(messages.withArguments("requests.workingTimeRequestApproval.unpaidTimeOffPlanned", formatDuration(timeAccountData.unpaidTimeOffDurationPlanned))).show();
		} else {
			this.dom.find("#workingTimeRequestApprovalDialogUnpaidTimeOffDurationPlanned").text("").hide();
		}
		if (timeAccountData && (timeAccountData.vacationYearFrom || timeAccountData.vacationYearTo)) {
			this.dom.find("#workingTimeRequestApprovalDialogVacationYear").text(formatDate(parseISODate(timeAccountData.vacationYearFrom)) + " " +
				messages["requests.workingTimeRequestApproval.vacationYear.to"] + " " +
				formatDate(parseISODate(timeAccountData.vacationYearTo)));
		} else {
			$("#workingTimeRequestApprovalVacationYear").text("-");
		}
		this.dom.find("#workingTimeRequestApprovalDialogVacationConsumed")
			.text(timeAccountData && timeAccountData.vacationConsumed != null ? formatVacation(timeAccountData.vacationConsumed, timeAccountData.vacationUnit) : "-");
		this.dom.find("#workingTimeRequestApprovalDialogVacationPlanned")
			.text(timeAccountData && timeAccountData.vacationPlanned != null ? formatVacation(timeAccountData.vacationPlanned, timeAccountData.vacationUnit) : "-");
		this.dom.find("#workingTimeRequestApprovalDialogVacationLeft")
			.text(timeAccountData && timeAccountData.vacationLeft != null ? formatVacation(timeAccountData.vacationLeft, timeAccountData.vacationUnit) : "-");
		this.dom.find(".vacationUnitLabel").text(timeAccountData && timeAccountData.vacationUnit ? messages["vacationUnit." + timeAccountData.vacationUnit] : "");
	}

	private checkForProofReviewAction(action: "approve" | "reject"): Promise<void> {
		if (!this.workingTimeRequest.proofId ||
			!SecurityStatus.isProofsAllowed() ||
			!WorkingTimeRequestUtil.isInApprovalProcess(this.workingTimeRequest)
		) {
			return Promise.resolve();
		}

		const selectedProofReviewAction = this.proofReviewActionButtonGroup.getValue();
		// Check if it is accepted for the last step as it is necessary
		// to accept the proof for approval of the last step.
		if (action === "approve" && selectedProofReviewAction !== ProofReviewAction.ACCEPTED &&
			![ProofStatus.OPTIONAL, ProofStatus.ACCEPTED].includes(this.workingTimeRequest.proofStatus) &&
			this.workingTimeRequest.currentApprovableStep?.step === this.workingTimeRequest.approvalSteps?.length - 1) {
			BootstrapModal.error({
				"message": "requests.workingTimeRequestApproval.proofReviewStatus.last.accepted.required"
			});
			return Promise.reject(new Error("proof review validation failed"));
		} else if (action === "approve" && selectedProofReviewAction === ProofReviewAction.REJECTED) {
			BootstrapModal.error({
				"message": "requests.workingTimeRequestApproval.proofReviewStatus.rejected.notAllowed"
			});
			return Promise.reject(new Error("proof review validation failed"));
		} else if (!selectedProofReviewAction) {
			BootstrapModal.error({
				"message": "requests.workingTimeRequestApproval.proofReviewStatus.required"
			});
			return Promise.reject(new Error("proof review validation failed"));
		} else {
			return Promise.resolve();
		}
	}

	private approve() {
		WorkingTimeRequestUtil.handleWorkingTimeRequestAction(
			"APPROVAL",
			this.workingTimeRequest,
			() => {
				this.onWorkingTimeRequestApproved();
				this.closeDialog();
			},
			"workingTimeRequestStatus.approve.title",
			"workingTimeRequestStatus.approve.message",
			WorkingTimeRequestEvent.APPROVE_REQUEST_STEP,
			this.workingTimeRequest.currentApprovableStep?.workingTimeRequestStepId,
			this.proofReviewActionButtonGroup.getValue(),
			"btn-success"
		);
	}

	private reject() {
		WorkingTimeRequestUtil.handleWorkingTimeRequestAction(
			"APPROVAL",
			this.workingTimeRequest,
			() => {
				this.onWorkingTimeRequestRejected();
				this.closeDialog();
			},
			"workingTimeRequestStatus.reject.title",
			"workingTimeRequestStatus.reject.message",
			WorkingTimeRequestEvent.REJECT_REQUEST_STEP,
			this.workingTimeRequest.currentApprovableStep?.workingTimeRequestStepId,
			this.proofReviewActionButtonGroup.getValue(),
			"btn-danger"
		);
	}

	private approveCancellation() {
		WorkingTimeRequestUtil.handleWorkingTimeRequestAction(
			"CANCELLATION",
			this.workingTimeRequest,
			() => {
				this.onWorkingTimeRequestApproved();
				this.closeDialog();
			},
			"workingTimeRequestStatus.approveCancellation.title",
			"workingTimeRequestStatus.approveCancellation.message",
			WorkingTimeRequestEvent.APPROVE_CANCELLATION_STEP,
			this.workingTimeRequest.currentApprovableStep?.workingTimeRequestStepId,
			null,
			"btn-success"
		);
	}

	private rejectCancellation() {
		WorkingTimeRequestUtil.handleWorkingTimeRequestAction(
			"CANCELLATION",
			this.workingTimeRequest,
			() => {
				this.onWorkingTimeRequestRejected();
				this.closeDialog();
			},
			"workingTimeRequestStatus.rejectCancellation.title",
			"workingTimeRequestStatus.rejectCancellation.message",
			WorkingTimeRequestEvent.REJECT_CANCELLATION_STEP,
			this.workingTimeRequest.currentApprovableStep?.workingTimeRequestStepId,
			null,
			"btn-danger"
		);
	}

	private onWorkingTimeRequestApproved() {
		$(this).triggerHandler(WorkingTimeRequestApprovalDialog.EVENT_REQUEST_APPROVED);
	}

	private onWorkingTimeRequestRejected() {
		$(this).triggerHandler(WorkingTimeRequestApprovalDialog.EVENT_REQUEST_REJECTED);
	}

	private initSubstituteValidation() {
		this.substituteValidation = document.getElementById("workingTimeRequestApprovalDialogSubstituteValidation") as SubstituteValidation;
		this.substituteValidation?.init(true);
	}

	private initProofs() {
		this.proofAttachmentGallery = this.dom.find("#workingTimeRequestApprovalDialogProofAttachmentGallery").get(0) as ProofAttachmentGallery;
		this.proofAttachmentGallery?.init("requests/getProofAttachments.json");
		this.proofAttachmentGallery?.setReadonly(true);

		const buttonRadioGroupDefinitions: TypedButtonDefinition<ProofReviewAction>[] = [];
		Object.values(ProofReviewAction).forEach(reviewAction => buttonRadioGroupDefinitions.push({
			button: document.getElementById("workingTimeRequestApprovalDialogProofReviewAction_" + reviewAction),
			value: reviewAction,
			selected: false
		}));
		this.proofReviewActionButtonGroup = ButtonRadioGroup.typed(...buttonRadioGroupDefinitions);
	}
}
